import type { IServiceGroupSettingsGetOne } from '@common/interfaces';
import { CustomControlCode } from '@common/enums';
import { ServiceCode } from '@common/enums/serviceCode';
import { fetchJson } from '@frontend/store/fetch';
import { getHasWriteAuthorizationForCustomControlCode, getServiceEmployments } from './serviceEmploymentsApiService';
import { isServiceProviderForServiceAndSetting } from './serviceProviderApiService';

export async function employmentHasAccessToHolterDashboard(employmentId: string, signal: AbortSignal) {
    const isHolterBiometrist = await isServiceProviderForServiceAndSetting(
        {
            employmentId,
            isActive: true,
            isBiometrist: true,
            serviceCode: ServiceCode.TCOH,
        },
        signal
    );
    const hasConfigureHolterWriteAuthorization = await getHasWriteAuthorizationForCustomControlCode(
        employmentId,
        [CustomControlCode.HolterConfigure, CustomControlCode.HolterDirectExaminationRequest],
        signal
    );

    return isHolterBiometrist || hasConfigureHolterWriteAuthorization;
}

export async function employmentHasAccessToHomePage(employmentId: string, signal: AbortSignal) {
    const isServiceProviderAndRequestCreator = await isServiceProviderForServiceAndSetting(
        {
            employmentId,
            isActive: true,
            isRequestCreator: true,
        },
        signal
    );

    if (isServiceProviderAndRequestCreator) {
        return true;
    }

    const url = `/api/serviceConsumers?employmentId=${employmentId}&isActive=true&isRequestCreator=true`;
    const serviceConsumers = await fetchJson<{ total: number }>({
        method: 'GET',
        signal,
        url,
    });

    const isServiceConsumerAndRequestCreator = (serviceConsumers.json?.total ?? 0) > 0;

    if (isServiceConsumerAndRequestCreator) {
        return true;
    }

    const serviceEmployments = await getServiceEmployments(employmentId, signal);

    return serviceEmployments.some((serviceEmployment) => serviceEmployment.canStartProcess);
}

export async function organizationHasAccessToFundusNoShows(organizationId: string, signal: AbortSignal) {
    const url = `/api/serviceGroupSettings/${organizationId}/${ServiceCode.TFO}/getCurrent`;

    const result = await fetchJson<IServiceGroupSettingsGetOne>({
        method: 'GET',
        signal,
        url,
    });

    return !!result?.json?.noShowsImported;
}
